import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Microphone, SpeakerHigh, Stop, SpinnerGap, Lock } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== Voice Record Button (STT) ==========
export const VoiceRecordButton = ({ user, onTranscription, disabled, language }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showPremiumHint, setShowPremiumHint] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const voiceEnabled = user?.voice_enabled;

  const handleClick = useCallback(async () => {
    if (!voiceEnabled) {
      setShowPremiumHint(true);
      setTimeout(() => setShowPremiumHint(false), 3000);
      return;
    }

    if (isRecording) {
      // Stop recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      return;
    }

    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Detect best supported format (Safari doesn't support webm)
      let mimeType = 'audio/webm;codecs=opus';
      let fileExt = 'webm';
      if (!MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
          fileExt = 'mp4';
        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
          mimeType = 'audio/aac';
          fileExt = 'aac';
        } else {
          mimeType = ''; // Let browser choose default
          fileExt = 'wav';
        }
      }

      const recorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
        }
        setIsRecording(false);

        const blobType = mimeType || 'audio/wav';
        const blob = new Blob(chunksRef.current, { type: blobType });
        if (blob.size < 100) return; // Too short

        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, `voice.${fileExt}`);
          formData.append('user_id', user?.id || '');

          const resp = await axios.post(`${API}/voice/stt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
          });

          if (resp.data?.text) {
            onTranscription(resp.data.text);
          }
        } catch (err) {
          void 0;
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.onerror = (e) => {
        void 0;
        setIsRecording(false);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
        }
      };

      mediaRecorder.start(250); // Collect chunks every 250ms
      setIsRecording(true);

      // Auto-stop after 60 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, 60000);
    } catch (err) {
      void 0;
    }
  }, [isRecording, voiceEnabled, user, onTranscription]);

  if (isTranscribing) {
    return (
      <motion.div
        className="relative flex items-center justify-center w-12 h-12"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      >
        <SpinnerGap size={24} className="text-starlight" weight="bold" />
      </motion.div>
    );
  }

  return (
    <div className="relative">
      <motion.button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        whileTap={{ scale: 0.9 }}
        className={`
          w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300
          ${isRecording
            ? 'bg-red-500/20 border-2 border-red-400 text-red-400 animate-pulse shadow-[0_0_20px_rgba(239,68,68,0.4)]'
            : voiceEnabled
              ? 'bg-starlight/10 border border-starlight/50 text-starlight hover:bg-starlight/20 hover:shadow-[0_0_15px_rgba(212,175,55,0.3)]'
              : 'bg-white/5 border border-white/10 text-white/30 cursor-not-allowed'
          }
          disabled:opacity-50
        `}
        data-testid="voice-record-btn"
      >
        {isRecording ? <Stop size={22} weight="fill" /> : 
         voiceEnabled ? <Microphone size={22} weight="duotone" /> : <Lock size={18} weight="duotone" />}
      </motion.button>

      {/* Premium hint tooltip */}
      <AnimatePresence>
        {showPremiumHint && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap
                       bg-[#1a1520] border border-starlight/30 rounded-lg px-3 py-2 text-xs text-starlight/80
                       shadow-lg z-50"
            data-testid="voice-premium-hint"
          >
            Premium feature
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// ========== TTS Play Button (on oracle messages) ==========
export const TTSPlayButton = ({ text, user }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  const voiceEnabled = user?.voice_enabled;
  if (!voiceEnabled) return null;

  const handlePlay = async () => {
    // If already playing, stop
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    // If we already have the audio cached, replay
    if (blobUrlRef.current) {
      const audio = new Audio(blobUrlRef.current);
      audioRef.current = audio;
      audio.onended = () => setIsPlaying(false);
      audio.play();
      setIsPlaying(true);
      return;
    }

    // Fetch TTS
    setIsLoading(true);
    try {
      // Strip markdown formatting for cleaner speech
      const cleanText = text
        .replace(/[*_~`#]/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .slice(0, 4000);

      const resp = await axios.post(`${API}/voice/tts`, {
        text: cleanText,
        user_id: user?.id || '',
        voice: 'sage'
      }, {
        responseType: 'blob',
        timeout: 30000
      });

      const blob = new Blob([resp.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => { setIsPlaying(false); setIsLoading(false); };
      audio.play();
      setIsPlaying(true);
    } catch (err) {
      void 0;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.button
      type="button"
      onClick={handlePlay}
      whileTap={{ scale: 0.9 }}
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-200
        ${isPlaying
          ? 'bg-starlight/20 text-starlight border border-starlight/40'
          : 'bg-white/5 text-white/40 hover:text-starlight/70 hover:bg-white/10 border border-transparent'
        }
      `}
      data-testid="tts-play-btn"
    >
      {isLoading ? (
        <SpinnerGap size={14} className="animate-spin" />
      ) : isPlaying ? (
        <Stop size={14} weight="fill" />
      ) : (
        <SpeakerHigh size={14} weight="duotone" />
      )}
      {isPlaying ? 'Stop' : isLoading ? '' : ''}
    </motion.button>
  );
};
